/**
 * CycleHistoryTable — Completed heat cycles derived from N1200 run_state
 *
 * Shows cycles detected from run_state transitions (0→1 start, 1→0 end).
 * Same source of truth as the barrel chart cycle detection.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { History } from 'lucide-react'
import { useCycleLogs } from '@/hooks/useCycleLogs'

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit'
})

function formatDuration(hours) {
  if (hours == null) return '-'
  if (hours < 1) return `${Math.round(hours * 60)}m`
  return `${hours.toFixed(1)}h`
}

export default function CycleHistoryTable() {
  const { logs, loading, error } = useCycleLogs()

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <History className="h-4 w-4" />
            Cycle History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-24 flex items-center justify-center text-muted-foreground text-sm">
            Loading...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <History className="h-4 w-4" />
            Cycle History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-destructive">{error}</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <History className="h-4 w-4" />
          Cycle History
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {logs.length === 0 ? (
          <div className="h-24 flex flex-col items-center justify-center text-muted-foreground text-sm gap-2 p-4">
            <History className="h-5 w-5" />
            <span>No completed cycles in the last 7 days</span>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cycle</TableHead>
                <TableHead>Barrel</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead>Start</TableHead>
                <TableHead>End</TableHead>
                <TableHead className="text-right">Duration</TableHead>
                <TableHead className="text-right">Energy</TableHead>
                <TableHead className="text-right">Avg Rate</TableHead>
                <TableHead className="text-right">Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log, i) => (
                <TableRow key={log.heat_cycle_id || i}>
                  <TableCell className="text-xs text-muted-foreground font-mono">{log.cycle_code}</TableCell>
                  <TableCell className="font-medium">{log.barrel_id}</TableCell>
                  <TableCell className="text-sm font-mono">
                    {log.batch_codes?.length > 0
                      ? log.batch_codes.join(', ')
                      : <span className="text-muted-foreground italic">—</span>}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">
                    {dateFormatter.format(new Date(log.started_at))}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">
                    {dateFormatter.format(new Date(log.ended_at))}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatDuration(log.duration_hours)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {log.energy_kwh != null ? `${log.energy_kwh.toFixed(1)} kWh` : '-'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {log.energy_kwh > 0 && log.cost_gbp != null
                      ? `${(log.cost_gbp / log.energy_kwh * 100).toFixed(1)}p/kWh`
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {log.cost_gbp != null ? `£${log.cost_gbp.toFixed(2)}` : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
